/**
 * 声音消息组件
 * @author sunzhiguang
 * @date 2020/6/10
 */
import React, { useState, useEffect, useMemo } from "react";
import { MessageSendType } from "@/types/chat/enum";
import "./index.less";
import { VoiceChatMessage } from "@/types/chat";
import { WithTranslation, withTranslation } from "react-i18next";
import { getFileUrl } from "@/utils/chat/index";
import getCurVoiceHandler, { VoiceStatus } from "../../handler/voiceHandler";
import classNames from "classnames";
import { IcBtnVoice } from "@/assets/svg";
import { connect } from "dvajs";
import ConnectState from "@jd/jdee.im.sdk/lib/es/enum/ConnectState";
import ChatState, { VoiceMessageMap } from "@/types/chat/State";
import ImService from "@/server/ImService";
import Bus from "@/utils/bus";
import {
  getVoiceMsgWidth,
  updateVoiceMessageReadStatus,
  voiceToTextFn
} from "@/utils/chat/message";
import IconFont from "@/components/icon";
import { LoadingOutlined } from "@/utils/antdIcon";
import { Spin } from "antd";
import { FocusSDK } from "@/utils";
interface VoiceProps {
  message: any;
  sendType: MessageSendType;
}
interface DvaDispatchProps {
  updateSessionMessageMid: Function;
  updateSessionMessagesTimestamp: Function;
  setVoiceMessageMap: Function;
}

type IVoiceProps = Readonly<VoiceProps & DvaDispatchProps & WithTranslation & ChatState>;

function Voice({
  message,
  sendType,
  connectState,
  selectedSession,
  updateSessionMessageMid,
  updateSessionMessagesTimestamp,
  voiceMessageMap,
  setVoiceMessageMap
}: IVoiceProps) {
  const [voiceStatus, setVoiceStatus] = useState(VoiceStatus.PLAY);
  const [isShowText, setIsShowText] = useState(false);
  const [isConverted, setIsConverted] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const isIntercome = useMemo(() => {
    return message.voiceType && message.voiceType === 1;
  }, [message]);

  const width = useMemo(() => {
    return getVoiceMsgWidth(message);
  }, [message]);

  const triggerLoading = () => {
    setLoading(true);
  };

  const changeVoiceConvertText = (result: string) => {
    setText(result);
    setIsConverted(!!result);
    setIsShowText(true);
    setLoading(false);
  };

  useEffect(() => {
    Bus.on(`voice:convertText:loading:${selectedSession.sessionId}:${message.mid}`, triggerLoading);
    Bus.on(`voice:convertText:${selectedSession.sessionId}:${message.mid}`, changeVoiceConvertText);
    return () => {
      Bus.off(
        `voice:convertText:loading:${selectedSession.sessionId}:${message.mid}`,
        triggerLoading
      );
      Bus.off(
        `voice:convertText:${selectedSession.sessionId}:${message.mid}`,
        changeVoiceConvertText
      );
    };
  }, [selectedSession, message]);

  useEffect(() => {
    const target = voiceMessageMap.find((item: VoiceMessageMap) => item.mid === message.mid);
    if (target) {
      setText(target.text);
      setIsConverted(!!target.text);
      setIsShowText(true);
    }
  }, [voiceMessageMap, message]);

  const handleSetReadTime = async () => {
    // 单聊密聊消息开启倒计时
    const readTimeOfMessage = new Date().getTime();
    const imService = ImService.getInstance();
    const result = await imService.updateReadTime(message.sessionId, message.mid, {
      ...message,
      readTimeOfMessage,
      isPlayOrTurn: true
    });
    if (result) {
      await updateSessionMessageMid({
        sessionId: message.sessionId,
        sendResult: {
          body: { ...message, readTimeOfMessage, isPlayOrTurn: true }
        }
      });
      await updateSessionMessagesTimestamp({
        timestamp: new Date().getTime()
      });
    }
  };

  function handleClickVoice(event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (connectState && connectState.state === ConnectState.READY) {
      // 设置消息读取时间
      const needManualSet =
        selectedSession?.isSecret &&
        sendType === MessageSendType.OTHER &&
        message?.expire &&
        !message.readTimeOfMessage;
      if (needManualSet) {
        handleSetReadTime();
      } else {
        updateVoiceMessageReadStatus(
          message,
          updateSessionMessageMid,
          updateSessionMessagesTimestamp
        );
      }
      const { id, url, urls = {} } = message;
      let src = "";
      if (urls?.opusOgg) {
        src = getFileUrl(urls?.opusOgg);
      } else {
        src = getFileUrl(url);
      }
      const voiceHandler = getCurVoiceHandler(id, src, {
        afterPlay: () => {
          // console.log('afterPlay');
          setVoiceStatus(VoiceStatus.STOP);
        },
        afterStop: () => {
          // console.log('afterStop');
          setVoiceStatus(VoiceStatus.PLAY);
        },
        type: urls?.opusOgg ? "opus" : ""
      });
      // console.log("voiceHandler", voiceHandler);
      // 绑定事件
      // voiceHandler?.voiceAmr?.onPlay(function () {
      //     console.log('event play');
      //     setVoiceStatus(VoiceStatus.STOP);
      // });
      // voiceHandler?.voiceAmr?.onStop(function () {
      //     console.log('event stop');
      //     setVoiceStatus(VoiceStatus.PLAY);
      // });
    }
  }
  function getClass() {
    return classNames({
      "voice-box": true,
      owner: sendType === MessageSendType.OWNER || sendType === MessageSendType.SELF,
      intercom: isIntercome
    });
  }

  function getVoiceStatus() {
    if (voiceStatus === VoiceStatus.STOP) {
      return <div className={`bg ${isIntercome ? "incomePlay" : "voicePlay"}`} />;
    }

    if (voiceStatus === VoiceStatus.PLAY) {
      // return <IcBtnVoice />;
      return <div className="bg" />;
    }
  }

  async function handleTurnText() {
    Bus.emit(`voice:convertText:loading:${selectedSession.sessionId}:${message.mid}`);
    await voiceToTextFn({
      session: selectedSession,
      message,
      map: voiceMessageMap,
      setMap: setVoiceMessageMap
    });
  }

  return (
    // <div className={getClass()} data-id={message.id} onClick={() => handleClickVoice(message)}>
    //     <div className="icon">{getVoiceStatus()}</div>
    //     <div className="time">{message.duration}&quot;</div>
    <div className={getClass()}>
      <div className="voice-box-audio" style={{ width: width }}>
        <div className="audio-container" data-id={message.id} onClick={handleClickVoice}>
          <div className="icon">{getVoiceStatus()}</div>
          <div className="time">{message.duration}&quot;</div>
        </div>
        {/* 语音转文字内容和状态放到这里 */}
      </div>
      {(isShowText || loading) && (
        <div className="audio-text-container">
          {loading ? (
            <div className="voice-loading">
              <Spin indicator={<LoadingOutlined style={{ fontSize: 22, color: "#999999" }} />} />
            </div>
          ) : (
            <>
              <div className="audio-text_content">{text}</div>
              {isConverted ? (
                <div className="audio-text_success">
                  <IconFont
                    style={{ color: "#F18F2E" }}
                    className="audio-text_icon"
                    type="iconlog_message"
                  />
                  <span>转换完成</span>
                </div>
              ) : (
                <div className="audio-text_retry" onClick={handleTurnText}>
                  <IconFont
                    style={{ color: "#F4222E" }}
                    className="audio-text_icon"
                    type="iconbtn_reduction"
                  />
                  <span>转换失败，请重试</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function mapStateToProps({ chat }: any) {
  return {
    connectState: chat.connectState,
    selectedSession: chat.selectedSession,
    voiceMessageMap: chat.voiceMessageMap
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { sendResult: any; sessionId: string }) {
      dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    },
    updateSessionMessagesTimestamp(data: { timestamp: number }) {
      dispatch({ type: "chat/updateSessionMessagesTimestamp", payload: data });
    },
    setVoiceMessageMap(payload: boolean) {
      dispatch({ type: "chat/setVoiceMessageMap", payload });
    }
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation("chat")(Voice));
